import { AxiosError } from 'axios';
import { explainKnownSqlError } from '@/shared/lib/octo/sqlError';

export type OctoErrorKind = 'network' | 'http' | 'business' | 'auth' | 'tenant';

/** Tüm API hataları bu tipe normalize edilir; UI yalnızca bunu tanır. */
export class OctoApiError extends Error {
  constructor(
    public readonly kind: OctoErrorKind,
    message: string,
    public readonly options: {
      status?: number;
      detail?: string | null;
      url?: string;
      raw?: unknown;
      /** Sunucunun ürettiği hata kimliği (X-Octo-Error-Id) — Seq'te aynı kaydı bulmaya yarar */
      errorId?: string | null;
    } = {},
  ) {
    super(message);
    this.name = 'OctoApiError';
  }
  get status() {
    return this.options.status;
  }
  get detail() {
    return this.options.detail ?? null;
  }
  /** Sunucu tarafındaki kaydın kimliği; kullanıcı destek hattına bunu söyler. */
  get errorId() {
    return this.options.errorId ?? null;
  }
}

/** SP/trigger mesajlarını "Mesaj :" ayracından ayırır (DataManipulationService.cs ile aynı). */
export function splitSqlMessage(raw: string): { message: string; detail: string | null } {
  // SP100_GenerateError satırları yalnız CR ile ayırıyor; tarayıcı tek başına CR'yi satır
  // sonu saymadığı için "Konum : XSatır : [14]" bitişik görünüyordu (#463).
  const full = raw.replace(/\r\n?/g, '\n');
  const m = full.match(/(Mesaj|Message)\s*:\s*/i);
  if (m && m.index != null) {
    const detail = full.substring(0, m.index).trim();
    const message = full.substring(m.index + m[0].length).trim();
    return { message: message || full, detail: detail || null };
  }
  return { message: full, detail: null };
}

function isTenantMessage(text: string) {
  return /bağlantı bilgisi taşınmıyor|bağlantısı artık geçerli değil/i.test(text);
}

export function toOctoError(err: unknown): OctoApiError {
  if (err instanceof OctoApiError) return err;
  if (err instanceof AxiosError) {
    const status = err.response?.status;
    const url = err.config?.url ?? '';
    const data = err.response?.data as unknown;
    const bodyText = typeof data === 'string' ? data : (data as { message?: string } | undefined)?.message ?? '';
    /*
     * Hata kimliği: sunucu her yanıta `X-Octo-Error-Id` koyuyor, hata gövdesinde de
     * `errorId` alanı var. Aynı kimlik Seq'teki sunucu kaydında duruyor; kullanıcının
     * ekranda gördüğü kod ile logdaki satır böyle eşleşiyor.
     */
    const errorId =
      (err.response?.headers?.['x-octo-error-id'] as string | undefined) ??
      (data as { errorId?: string } | undefined)?.errorId ??
      null;
    if (!err.response) return new OctoApiError('network', 'Sunucuya ulaşılamıyor.', { url, raw: err });
    if (status === 401 && isTenantMessage(bodyText)) return new OctoApiError('tenant', bodyText, { status, url, raw: err, errorId });
    if (status === 401 || status === 403) return new OctoApiError('auth', bodyText || 'Yetkisiz istek.', { status, url, raw: err, errorId });
    if (status === 503) return new OctoApiError('http', bodyText || 'Sunucu geçici olarak kullanılamıyor.', { status, url, raw: err, errorId });
    const known = explainKnownSqlError(bodyText);
    if (known) return new OctoApiError('http', known, { status, detail: bodyText, url, raw: err, errorId });
    const { message, detail } = splitSqlMessage(bodyText || err.message);
    return new OctoApiError('http', message, { status, detail, url, raw: err, errorId });
  }
  if (err instanceof Error) return new OctoApiError('network', err.message, { raw: err });
  return new OctoApiError('network', String(err), { raw: err });
}
